import { useEffect, useState } from "react";
import { Bell, X, CheckCheck } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";

export default function NotificationsBell() {
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);

  const fetchNotifs = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data || []);
    } catch (e) { console.error("Failed to fetch notifications:", e); }
  };

  useEffect(() => {
    fetchNotifs();
    const t = setInterval(fetchNotifs, 60_000);
    return () => clearInterval(t);
  }, []);

  const act = async (id, action) => {
    try {
      await api.post(`/notifications/${id}/action`, { action });
      setNotifs((p) => p.filter((n) => n.id !== id));
    } catch (e) { console.error("Notification action failed:", e); }
  };

  const count = notifs.length;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-md hover:bg-[#F1F5F9] transition-colors"
        data-testid="notifications-bell"
      >
        <Bell className="w-5 h-5 text-[#475569]" />
        {count > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-[#EF4444] text-white text-[10px] rounded-full flex items-center justify-center font-medium" data-testid="notifications-count">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-[360px] max-h-[480px] bg-white border border-[#E2E8F0] rounded-lg shadow-xl z-50 overflow-hidden flex flex-col" data-testid="notifications-panel">
            <div className="px-4 py-3 border-b border-[#E2E8F0] flex items-center justify-between">
              <div>
                <div className="text-[11px] font-mono uppercase tracking-wider text-[#64748B]">Notifications</div>
                <div className="text-sm font-medium">{count} pending</div>
              </div>
              <Link to="/notifications" onClick={() => setOpen(false)} className="text-xs text-[#0055FF] hover:underline" data-testid="view-all-notifs">View all</Link>
            </div>
            <div className="overflow-y-auto flex-1">
              {notifs.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-[#64748B]">You're all caught up.</div>
              ) : notifs.slice(0, 8).map((n) => (
                <div key={n.id} className="px-4 py-3 border-b border-[#E2E8F0] hover:bg-[#F8F9FA]" data-testid={`notif-${n.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#0A0A0A] truncate">{n.title}</div>
                      {n.subtitle && <div className="text-xs text-[#475569] truncate">{n.subtitle}</div>}
                      {n.detail && <div className="text-[11px] text-[#64748B] mt-0.5 line-clamp-2">{n.detail}</div>}
                      <div className="text-[10px] font-mono text-[#94A3B8] mt-1">{n.due_days}d ago · {n.type}</div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <button
                        type="button"
                        onClick={() => act(n.id, "completed")}
                        className="p-1 rounded hover:bg-green-50 text-green-600"
                        title="Mark completed"
                        data-testid={`notif-complete-${n.id}`}
                      >
                        <CheckCheck className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => act(n.id, "dismissed")}
                        className="p-1 rounded hover:bg-slate-100 text-[#64748B]"
                        title="Dismiss"
                        data-testid={`notif-dismiss-${n.id}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
